import {Component} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {PasswordRecoveryApiService} from '@npck/af-frontend-api';
import {RouterService, rxSpinner, toBusinessOrNull, TypedForm} from '@npck/libs/commons';
import {Path} from '../../../path';

const EMAIL_METHOD = 'EMAIL';
const BIOMETRIC_METHOD = 'BIOMETRIC';
const USER_NOT_FOUND_CODES = ['AF_PASSWORD_RECOVERY_USER_NOT_FOUND', 'AF_PASSWORD_RECOVERY_EMAIL_NOT_AVAILABLE'];
const PHONE_NOT_AVAILABLE = 'AF_PASSWORD_RECOVERY_PHONE_NOT_AVAILABLE';
const IIN_BLOCKED = 'IIN_BLOCKED';

@Component({
    selector: 'af-password-recovery',
    standalone: false,
    templateUrl: './password-recovery.component.html',
    styleUrl: './password-recovery.component.scss',
})
export class PasswordRecoveryComponent {
    readonly EMAIL_METHOD = EMAIL_METHOD;
    readonly BIOMETRIC_METHOD = BIOMETRIC_METHOD;
    form: TypedForm<PasswordRecoveryForm>;
    emailSent = false;
    recoveryErrorKey = '';

    constructor(
        private formBuilder: FormBuilder,
        private routerService: RouterService,
        private passwordRecoveryApi: PasswordRecoveryApiService,
    ) {
        this.buildForm();
    }

    submit(): void {
        if (this.form.value.method === BIOMETRIC_METHOD) {
            this.submitBiometric();
        } else {
            this.submitEmail();
        }
    }

    cancel(): void {
        this.routerService.navigate(Path.LOGIN);
    }

    private submitEmail(): void {
        this.passwordRecoveryApi.initiatePasswordRecoveryByEmail({username: this.form.value.login})
            .pipe(rxSpinner('submit'))
            .subscribe({
                next: () => this.emailSent = true,
                error: err => {
                    const businessError = toBusinessOrNull(err);
                    if (businessError && USER_NOT_FOUND_CODES.includes(businessError.error)) {
                        this.showFieldError('login', 'i18n_af_errorUserNotFound');
                    } else {
                        throw err;
                    }
                },
            });
    }

    private submitBiometric(): void {
        this.passwordRecoveryApi.initiatePasswordRecoveryByBiometric({iin: this.form.value.iin, frontendHost: window.location.host})
            .pipe(rxSpinner('submit'))
            .subscribe({
                next: response => window.location.href = response.url,
                error: err => {
                    const businessError = toBusinessOrNull(err);
                    const code: string | undefined = businessError?.error;
                    if (code === PHONE_NOT_AVAILABLE) {
                        this.showFieldError('iin', 'i18n_af_errorPhoneNotAvailable');
                    } else if (code && USER_NOT_FOUND_CODES.includes(code)) {
                        this.showFieldError('iin', 'i18n_af_errorIinNotFound');
                    } else if (code === IIN_BLOCKED) {
                        this.showFieldError('iin', 'i18n_af_errorIinBlocked');
                    } else {
                        throw err;
                    }
                },
            });
    }

    private showFieldError(controlName: 'login' | 'iin', messageKey: string): void {
        this.recoveryErrorKey = messageKey;
        const control = this.form.controls[controlName];
        control.setErrors({recoveryError: true});
        control.markAsTouched();
        const subscription = control.valueChanges.subscribe(() => {
            control.setErrors(null);
            control.updateValueAndValidity({emitEvent: false});
            subscription.unsubscribe();
        });
    }

    private buildForm(): void {
        this.form = this.formBuilder.group({
            method: new FormControl(null, [Validators.required]),
            login: new FormControl(''),
            iin: new FormControl(''),
        });
        this.form.controls.method.valueChanges.subscribe(method => this.applyMethodValidators(method));
    }

    private applyMethodValidators(method: string): void {
        const login = this.form.controls.login;
        const iin = this.form.controls.iin;
        if (method === BIOMETRIC_METHOD) {
            iin.setValidators([Validators.required]);
            login.clearValidators();
        } else {
            login.setValidators([Validators.required]);
            iin.clearValidators();
        }
        login.updateValueAndValidity({emitEvent: false});
        iin.updateValueAndValidity({emitEvent: false});
    }
}

interface PasswordRecoveryForm {
    method: string;
    login: string;
    iin: string;
}
